/**
 * Theme loader utility
 * Handles dynamic theme loading based on VITE_THEME environment variable
 */

const AVAILABLE_THEMES = ['black_friday', 'winter_theme', 'halloween', 'spring'];

// Safe default returned when no theme is configured or the theme config
// cannot be loaded (e.g. stale cached bundle pointing at a chunk path that
// no longer exists, in which case the SPA fallback returns text/html).
// Always an object with a frozen `components` map so `theme.components[id]`
// is never a deref on undefined.
export const EMPTY_THEME = Object.freeze({
  name: null,
  themeClass: '',
  socialIconColor: 'gold',
  hasStylesheet: false,
  components: Object.freeze({}),
});

// SPA-fallback / network errors look like real failures but are operational,
// not bugs. Real bugs in theme.config.js (SyntaxError etc.) should still log
// at error level so we hear about them.
function isOperationalLoadFailure(error) {
  const msg = String(error?.message || '');
  return /MIME type|Failed to fetch|NetworkError|Importing a module script failed|error loading dynamically imported module/i.test(msg);
}

/**
 * Get the current theme from environment variable
 * @returns {string|null} Theme name or null for default theme
 */
export function getCurrentTheme() {
  const theme = import.meta.env.VITE_THEME;

  // Return null for default theme
  if (!theme || theme === 'default' || theme === '') {
    return null;
  }

  // Validate theme exists
  if (!AVAILABLE_THEMES.includes(theme)) {
    console.warn(`Theme "${theme}" not found. Available themes:`, AVAILABLE_THEMES);
    return null;
  }

  return theme;
}

/**
 * Load theme configuration
 * @param {string} themeName - Name of the theme to load
 * @returns {Promise<Object>} Theme configuration object
 */
export async function loadThemeConfig(themeName) {
  if (!themeName) {
    return EMPTY_THEME;
  }

  try {
    const mod = await import(`./themes/${themeName}/theme.config.js`);
    const cfg = mod.default ?? mod;
    return {
      ...EMPTY_THEME,
      ...cfg,
      components: { ...(cfg?.components ?? {}) },
    };
  } catch (error) {
    const msg = String(error?.message ?? error);
    if (isOperationalLoadFailure(error)) {
      // Stale cached bundle pointing at a missing chunk, or a transient network
      // failure. Degrade silently to default theme; log at warning level so the
      // population is still visible in monitoring.
      console.warn(`Theme "${themeName}" not loadable, using default:`, msg);
      if (typeof Rollbar !== 'undefined') Rollbar.warning(`Theme asset missing: ${themeName} (${msg})`);
    } else {
      console.error(`Failed to load theme config for "${themeName}":`, error);
      if (typeof Rollbar !== 'undefined') Rollbar.error(`Theme config error: ${themeName} ${msg}`);
    }
    return EMPTY_THEME;
  }
}

/**
 * Get theme stylesheet path
 * @param {string} themeName - Name of the theme
 * @returns {string|null} Path to theme stylesheet or null
 */
export function getThemeStylesheet(themeName) {
  if (!themeName) {
    return null;
  }

  return `./themes/${themeName}/theme.scss`;
}

/**
 * Check if current theme has a specific component
 * @param {{components?: Record<string, boolean>} | null | undefined} themeConfig - Theme configuration object
 * @param {string} componentName - Name of the component to check
 * @returns {boolean}
 */
export function themeHasComponent(themeConfig, componentName) {
  return themeConfig?.components?.[componentName] === true;
}

/**
 * Get list of available themes
 * @returns {Array<string>}
 */
export function getAvailableThemes() {
  return AVAILABLE_THEMES;
}
